import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const initialState = {
  leapParticipationConsents: [],
  currentLeapParticipationConsent: null,
  isLoading: false,
  error: null,
};

export const fetchLeapParticipationConsents = createAsyncThunk(
  'leapParticipationConsent/fetchAll',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/leap-participation-consents/${patientId}`
        : '/leap-participation-consents';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch LEAP Participation Consent');
    }
  }
);

export const fetchLeapParticipationConsent = createAsyncThunk(
  'leapParticipationConsent/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/leap-participation-consents/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch LEAP Participation Consent');
    }
  }
);

export const createLeapParticipationConsent = createAsyncThunk(
  'leapParticipationConsent/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/leap-participation-consents', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to create LEAP Participation Consent');
    }
  }
);

export const updateLeapParticipationConsent = createAsyncThunk(
  'leapParticipationConsent/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/leap-participation-consents/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to update LEAP Participation Consent');
    }
  }
);

export const deleteLeapParticipationConsent = createAsyncThunk(
  'leapParticipationConsent/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/leap-participation-consents/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete LEAP Participation Consent');
    }
  }
);

const slice = createSlice({
  name: 'leapParticipationConsent',
  initialState,
  reducers: {
    clearCurrentLeapParticipationConsent: (state) => { state.currentLeapParticipationConsent = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeapParticipationConsents.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchLeapParticipationConsents.fulfilled, (s, a) => { s.isLoading = false; s.leapParticipationConsents = a.payload; })
      .addCase(fetchLeapParticipationConsents.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(fetchLeapParticipationConsent.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchLeapParticipationConsent.fulfilled, (s, a) => { s.isLoading = false; s.currentLeapParticipationConsent = a.payload; })
      .addCase(fetchLeapParticipationConsent.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(createLeapParticipationConsent.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(createLeapParticipationConsent.fulfilled, (s, a) => { s.isLoading = false; s.leapParticipationConsents.unshift(a.payload); })
      .addCase(createLeapParticipationConsent.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(updateLeapParticipationConsent.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(updateLeapParticipationConsent.fulfilled, (s, a) => {
        s.isLoading = false;
        s.currentLeapParticipationConsent = a.payload;
        const i = s.leapParticipationConsents.findIndex((r) => r.id === a.payload.id);
        if (i !== -1) s.leapParticipationConsents[i] = a.payload;
      })
      .addCase(updateLeapParticipationConsent.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(deleteLeapParticipationConsent.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(deleteLeapParticipationConsent.fulfilled, (s, a) => { s.isLoading = false; s.leapParticipationConsents = s.leapParticipationConsents.filter((r) => r.id !== a.payload.id); })
      .addCase(deleteLeapParticipationConsent.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; });
  },
});

export const { clearCurrentLeapParticipationConsent, clearError } = slice.actions;
export default slice.reducer;
